/**
 * OfflineBanner — Network status indicator (#114)
 *
 * Shows a non-intrusive banner when the browser goes offline.
 * Auto-hides when connectivity is restored.
 *
 * @see Issue #114
 */
import { useState, useEffect } from "react";
import { flushWriteQueue } from "@/lib/write-through-queue";
import { getSupabase } from "@/lib/supabase";

export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showReconnect, setShowReconnect] = useState(false);

  useEffect(() => {
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    const handleOffline = () => {
      setIsOffline(true);
      setShowReconnect(false);
    };
    const handleOnline = () => {
      setIsOffline(false);
      setShowReconnect(true);
      // #688: Flush queued writes on reconnect
      flushWriteQueue(() => getSupabase());
      // #547: Clear previous timer before creating new one to prevent orphan timers
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(() => setShowReconnect(false), 3000);
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, []);

  if (!isOffline && !showReconnect) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={`fixed top-14 inset-x-0 z-[var(--z-dropdown)] flex items-center justify-center gap-2 py-2 px-4 text-xs font-medium transition-colors ${
        isOffline
          ? "bg-amber-50 text-amber-800 border-b border-amber-200"
          : "bg-emerald-50 text-emerald-700 border-b border-emerald-200"
      }`}
    >
      {isOffline ? (
        <>
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <span>网络连接已断开，部分功能可能不可用</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>网络已恢复</span>
        </>
      )}
    </div>
  );
}
